import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { buildYup } from 'schema-to-yup';
import config from './config';
import { useCreateUserGroupV2, useGetUserGroupV2, usePreviewUserGroup, useUpdateUserGroupV2 } from '../../../main/apps/RoleSpecificApps/Admin/user-groups/UserGroupsApp/UserGroup.queries';
import FormController from '../controller/FormController';
import FormCreator from '../../../configured-components/form/configured-inputs/form-creator/FormCreator';
import { getUser, getUserRegion } from '../../../../utils';
import MMSplashScreen from '../../MMSplashScreen';
import { NotificationContext } from '../../../NotificationContext';
import { Button } from '@material-ui/core';
import { useFormContext } from 'react-hook-form';

const configRegionMap = {};

function PreviewButton({ onPreview, isLoading }) {
    const { getValues } = useFormContext();

    return (
        <Button variant="outlined" color="primary" onClick={() => onPreview(getValues)} disabled={isLoading}>
            {isLoading ? 'Previewing...' : 'Preview'}
        </Button>
    );
}

function PreviewDataUpdater({ previewUsers }) {
    const { setValue } = useFormContext();

    useEffect(() => {
        setValue('user_preview', previewUsers);
    }, [previewUsers, setValue]);

    return null;
}

function UserGroupForm({ ...props }) {
    const user = getUser();
    const userRegion = getUserRegion();
    const regionConfig = configRegionMap[userRegion] || config.defaultForm;
    const validation = buildYup(regionConfig?.validation, regionConfig?.validation_config);
    const { ignore_on_submit_fields } = regionConfig;
    const { id } = useParams('id');
    const history = useHistory();
    const [previewUsers, setPreviewUsers] = useState(null);
    const { showSnackbar } = useContext(NotificationContext);

    const {
        data: userGroupData,
        isLoading: isLoadingUserGroup,
        error: userGroupError,
    } = useGetUserGroupV2({
        id,
        enabled: !!id,
    });
    const createUserGroupMutation = useCreateUserGroupV2();
    const updateUserGroupMutation = useUpdateUserGroupV2();
    const previewUserGroupMutation = usePreviewUserGroup();

    const transformUserGroupData = (data) => {
        if (!data) return data;

        const { type } = data;

        return {
            ...data,
            type: type || 'producer',
        };
    };

    const userGroup = transformUserGroupData(userGroupData) || props?.defaultValues;

    useEffect(() => {
        if (userGroupError) {
            let message = 'Error fetching user group data.';
            if (userGroupError.response?.status === 404) {
                message = 'User group not found.';
            } else if (userGroupError.response?.status === 500) {
                message = 'Server error. Check user group ID and try again.';
            }
            showSnackbar({ severity: 'error', message });
        }
    }, [userGroupError, showSnackbar]);

    const handleSubmit = async (model) => {
        try {
            const chosenType = model.type;
            const transformedModel = {
                name: model.name,
                type: chosenType,
            };

            if (model.description && model.description.trim() !== '') {
                transformedModel.description = model.description;
            }

            if (model.is_dynamic) {
                if (!model.filter?.groups?.length) {
                    showSnackbar({ severity: 'error', message: 'Please provide a filter for a dynamic group' });
                    return { error: 'No filter provided for dynamic group' };
                }

                transformedModel.is_dynamic = true;
                transformedModel.users = [];
                transformedModel.filter = model.filter;
            } else {
                if (!model.users?.length) {
                    showSnackbar({ severity: 'error', message: 'Please select at least one user for a static group' });
                    return { error: 'No users selected for static group' };
                }

                transformedModel.is_dynamic = false;
                transformedModel.users = model.users;
                transformedModel.filter = null;
            }

            let resp;
            if (id) {
                resp = await updateUserGroupMutation.mutateAsync({ id, model: transformedModel });
                showSnackbar({ severity: 'success', message: 'User group updated successfully' });
            } else {
                resp = await createUserGroupMutation.mutateAsync(transformedModel);
                showSnackbar({ severity: 'success', message: 'User group created successfully' });
            }

            history.push('/user-groups');
            return resp;
        } catch (error) {
            return { error: error?.message || 'Error submitting form' };
        }
    };

    const handleCancel = () => {
        history.push({ pathname: '/user-groups' });
    };

    const handlePreview = async (getValues) => {
        try {
            const formValues = getValues();

            if (!formValues.is_dynamic) {
                showSnackbar({ severity: 'info', message: 'Preview is only available for dynamic groups' });
                return;
            }

            const previewData = {
                name: 'preview',
                description: 'description',
                is_dynamic: true,
                type: formValues.type,
                filter: formValues.filter || { groups: [] },
            };

            const { users } = await previewUserGroupMutation.mutateAsync(previewData);
            setPreviewUsers(users || []);
        } catch (error) {
            setPreviewUsers(null);
        }
    };

    const loading = id && isLoadingUserGroup;
    if (loading || !user || !regionConfig) return <MMSplashScreen />;

    const additionalFormActions = {
        middle: <PreviewButton onPreview={handlePreview} isLoading={previewUserGroupMutation.isLoading} />,
    };

    return (
        <FormController {...props} schema={validation} defaultValues={{ ...userGroup, user_preview: previewUsers }} submit={handleSubmit} handleCancel={handleCancel} shouldUnregister submitText={'Save'} ignoreOnSubmitFields={!!id ? ignore_on_submit_fields?.edit : ignore_on_submit_fields?.create} AdditionalFormActions={additionalFormActions}>
            <PreviewDataUpdater previewUsers={previewUsers} />
            <FormCreator config={regionConfig} user={user} isEdit={!!id} />
        </FormController>
    );
}

export default UserGroupForm;
